import React, { useState, useEffect } from "react";
import { bulkUpdate } from "../api";

// REAL API CALL — AlAdhan Prayer Times API
async function fetchPrayerTimesForDate({ latitude, longitude, madhab, date }) {
  const method = 2; // ISNA
  const school = madhab === "Hanafi" ? 1 : 0;

  const url = `https://api.aladhan.com/v1/timings/${date}?latitude=${latitude}&longitude=${longitude}&method=${method}&school=${school}`;

  const res = await fetch(url);
  const json = await res.json();

  if (!json || !json.data) throw new Error("Invalid API response");

  const t = json.data.timings;

  return {
    fajr: t.Fajr,
    sunrise: t.Sunrise,
    dhuhr: t.Dhuhr,
    asr: t.Asr,
    maghrib: t.Maghrib,
    isha: t.Isha,
    jumuah: t.Dhuhr,
  };
}

// ⭐ Helper: add minutes to HH:MM
function addMinutes(time, mins) {
  if (!time || mins === "" || isNaN(mins)) return "";
  const [h, m] = time.split(":").map(Number);
  let total = h * 60 + m + Number(mins);
  if (total < 0) total = 0;
  if (total >= 24 * 60) total = 24 * 60 - 1;
  const hh = String(Math.floor(total / 60)).padStart(2, "0");
  const mm = String(total % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

export default function SalahByLocation() {
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [madhab, setMadhab] = useState("Hanafi");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [status, setStatus] = useState("");

  const [rows, setRows] = useState([]);

  // ⭐ DEFAULT IQAMAH TIMES
  const [defaultIqamah, setDefaultIqamah] = useState({
    fajrIqamah: "",
    dhuhrIqamah: "",
    asrIqamah: "",
    maghribIqamah: "",
    ishaIqamah: "",
    jumuahIqamah: "",
  });

  // ⭐ MAGHRIB OFFSET
  const [maghribOffset, setMaghribOffset] = useState("");

  function updateDefaultIqamah(field, value) {
    setDefaultIqamah((prev) => ({ ...prev, [field]: value }));
  }

  // COPY MODE
  const [copyMode, setCopyMode] = useState(false);
  const [copiedValue, setCopiedValue] = useState("");

  // ESC cancels copy mode
  useEffect(() => {
    function handleEsc(e) {
      if (e.key === "Escape") {
        setCopyMode(false);
        setCopiedValue("");
      }
    }
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  function updateRow(idx, field, value) {
    setRows((prev) => {
      const next = [...prev];
      next[idx][field] = value;
      return next;
    });
  }

  function handleCellClick(idx, field, value) {
    if (!copyMode) return;

    if (copiedValue === "") {
      setCopiedValue(value);
    } else {
      updateRow(idx, field, copiedValue);
    }
  }

  async function onFetch() {
    if (!latitude || !longitude) {
      setStatus("Enter latitude & longitude");
      return;
    }
    if (!startDate || !endDate) {
      setStatus("Select date range");
      return;
    }

    setStatus("Fetching prayer times...");

    // ⭐ FIXED DATE PARSING
    const [sy, sm, sd] = startDate.split("-");
    const [ey, em, ed] = endDate.split("-");

    const start = new Date(Number(sy), Number(sm) - 1, Number(sd));
    const end = new Date(Number(ey), Number(em) - 1, Number(ed));

    if (start > end) {
      setStatus("Start date must be before end date");
      return;
    }

    const temp = [];
    let current = new Date(start);

    while (current <= end) {
      const yyyy = current.getFullYear();
      const mm = String(current.getMonth() + 1).padStart(2, "0");
      const dd = String(current.getDate()).padStart(2, "0");

      const apiDate = `${dd}-${mm}-${yyyy}`;
      const displayDate = `${mm}/${dd}/${String(yyyy).slice(-2)}`;

      try {
        const data = await fetchPrayerTimesForDate({
          latitude,
          longitude,
          madhab,
          date: apiDate,
        });

        temp.push({
          date: displayDate,
          day: current.toLocaleDateString("en-US", { weekday: "long" }),

          fajr: data.fajr,
          fajrIqamah: defaultIqamah.fajrIqamah,

          sunrise: data.sunrise,

          dhuhr: data.dhuhr,
          dhuhrIqamah: defaultIqamah.dhuhrIqamah,

          asr: data.asr,
          asrIqamah: defaultIqamah.asrIqamah,

          maghrib: data.maghrib,
          maghribIqamah:
            maghribOffset !== ""
              ? addMinutes(data.maghrib, maghribOffset)
              : defaultIqamah.maghribIqamah,

          isha: data.isha,
          ishaIqamah: defaultIqamah.ishaIqamah,

          jumuah: data.jumuah,
          jumuahIqamah: defaultIqamah.jumuahIqamah,
        });
      } catch (e) {
        console.error(e);
      }

      // ⭐ FIXED DATE LOOP
      current = new Date(
        current.getFullYear(),
        current.getMonth(),
        current.getDate() + 1,
      );
    }

    setRows(temp);
    setStatus("Prayer times loaded.");
  }

  // ⭐ UPLOAD TO DATABASE (WITH ALERT)
  async function uploadToDatabase() {
    try {
      setStatus("Uploading to database...");
      await bulkUpdate(rows);

      // ⭐ NEW ALERT
      alert("Successfully uploaded to database");

      setStatus("Successfully uploaded ✔");
    } catch (e) {
      setStatus("Upload failed: " + e.message);
    }
  }

  return (
    <div style={{ padding: "20px", maxWidth: "100%", margin: "0 auto" }}>
      <h1 style={{ textAlign: "center" }}>Salah & Iqamah Times by Location</h1>

      {/* LOCATION + MADHAB + DATE RANGE */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, 1fr)",
          gap: "15px",
          marginTop: "20px",
        }}
      >
        <div>
          <label>Latitude</label>
          <input
            type="number"
            step="0.000001"
            value={latitude}
            onChange={(e) => setLatitude(e.target.value)}
            style={{ width: "100%", padding: "6px" }}
          />
        </div>

        <div>
          <label>Longitude</label>
          <input
            type="number"
            step="0.000001"
            value={longitude}
            onChange={(e) => setLongitude(e.target.value)}
            style={{ width: "100%", padding: "6px" }}
          />
        </div>

        <div>
          <label>Madhab</label>
          <select
            value={madhab}
            onChange={(e) => setMadhab(e.target.value)}
            style={{ width: "100%", padding: "6px" }}
          >
            <option value="Hanafi">Hanafi</option>
            <option value="Shafi">Shafi</option>
            <option value="Maliki">Maliki</option>
            <option value="Hanbali">Hanbali</option>
          </select>
        </div>

        <div>
          <label>Date From</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{ width: "100%", padding: "6px" }}
          />
        </div>

        <div>
          <label>Date To</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{ width: "100%", padding: "6px" }}
          />
        </div>
      </div>

      {/* DEFAULT IQAMAH TIME PICKERS */}
      <h3 style={{ marginTop: "30px", textAlign: "center" }}>
        Default Iqamah Times (Applied to all fetched days)
      </h3>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "15px",
          maxWidth: "800px",
          margin: "0 auto",
        }}
      >
        {[
          ["Fajr Iqamah", "fajrIqamah"],
          ["Dhuhr Iqamah", "dhuhrIqamah"],
          ["Asr Iqamah", "asrIqamah"],
          ["Maghrib Iqamah", "maghribIqamah"],
          ["Isha Iqamah", "ishaIqamah"],
          ["Jumuah Iqamah", "jumuahIqamah"],
        ].map(([label, field]) => (
          <div key={field}>
            <label>{label}</label>
            <input
              type="time"
              value={defaultIqamah[field]}
              onChange={(e) => updateDefaultIqamah(field, e.target.value)}
              style={{ width: "100%", padding: "6px" }}
            />
          </div>
        ))}
      </div>

      {/* MAGHRIB OFFSET */}
      <h3 style={{ marginTop: "20px", textAlign: "center" }}>
        Maghrib Iqamah Offset (minutes)
      </h3>

      <div style={{ textAlign: "center", marginBottom: "20px" }}>
        <input
          type="number"
          value={maghribOffset}
          onChange={(e) => setMaghribOffset(e.target.value)}
          placeholder="e.g. 10"
          style={{
            width: "120px",
            padding: "6px",
            fontSize: "16px",
            textAlign: "center",
          }}
        />
        <div style={{ fontSize: "12px", color: "#666" }}>
          If filled, Maghrib Iqamah = Maghrib + this many minutes
        </div>
      </div>

      <div style={{ marginTop: "15px", textAlign: "center" }}>
        <button onClick={onFetch} style={{ padding: "8px 20px" }}>
          Fetch Prayer Times
        </button>
      </div>

      <div style={{ marginTop: "10px", textAlign: "center", color: "#555" }}>
        {status}
      </div>

      {/* COPY MODE BUTTON */}
      <div style={{ textAlign: "center", marginTop: "20px" }}>
        <button
          onClick={() => {
            setCopyMode(!copyMode);
            if (!copyMode) setCopiedValue("");
          }}
          style={{
            padding: "8px 20px",
            background: copyMode ? "red" : "green",
            color: "white",
          }}
        >
          {copyMode ? "Exit Copy Mode" : "Enter Copy Mode"}
        </button>
      </div>

      {copyMode && (
        <div style={{ textAlign: "center", marginTop: "5px", color: "blue" }}>
          Copy Mode Active — Click a box to copy, click another to paste. Press
          ESC to cancel.
        </div>
      )}

      {/* TABLE */}
      {rows.length > 0 && (
        <>
          <h3 style={{ marginTop: "30px", textAlign: "center" }}>
            Editable Prayer & Iqamah Times
          </h3>

          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr style={{ background: "#eee" }}>
                <th>Date</th>
                <th>Day</th>
                <th>Fajr</th>
                <th>Fajr Iqamah</th>
                <th>Sunrise</th>
                <th>Dhuhr</th>
                <th>Dhuhr Iqamah</th>
                <th>Asr</th>
                <th>Asr Iqamah</th>
                <th>Maghrib</th>
                <th>Maghrib Iqamah</th>
                <th>Isha</th>
                <th>Isha Iqamah</th>
                <th>Jumuah</th>
                <th>Jumuah Iqamah</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx}>
                  <td>{r.date}</td>
                  <td>{r.day}</td>

                  {[
                    "fajr",
                    "fajrIqamah",
                    "sunrise",
                    "dhuhr",
                    "dhuhrIqamah",
                    "asr",
                    "asrIqamah",
                    "maghrib",
                    "maghribIqamah",
                    "isha",
                    "ishaIqamah",
                    "jumuah",
                    "jumuahIqamah",
                  ].map((field) => (
                    <td key={field}>
                      <input
                        type="time"
                        value={r[field]}
                        onClick={() => handleCellClick(idx, field, r[field])}
                        onChange={(e) => updateRow(idx, field, e.target.value)}
                        style={{
                          cursor: copyMode ? "copy" : "text",
                          background: copyMode ? "#e0f7ff" : "white",
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {/* ⭐ UPLOAD TO DATABASE */}
          <div style={{ textAlign: "center", marginTop: "30px" }}>
            <button
              onClick={uploadToDatabase}
              style={{
                padding: "12px 30px",
                background: "#007bff",
                color: "white",
                fontSize: "16px",
                borderRadius: "6px",
              }}
            >
              Upload to Database
            </button>
          </div>
        </>
      )}
    </div>
  );
}
